import { useChat } from '@ai-sdk/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RESEARCH_MODE_AGUI_ENDPOINT } from 'api/constants';
import { createUserMessage } from 'modules/shared/libs/chat/utils/chat.utils';

import { useLazyGetResearchThreadQuery } from '../api/research-threads.api';
import { ResearchTransport } from '../transports/ResearchTransport';
import { convertResearchApiMessagesToUIMessages } from '../utils/message-conversion.utils';

import type { ChatStatus, ChatUIMessage } from 'modules/shared/libs/chat/types';

export type UseResearchChatReturnType = {
  messages: ChatUIMessage[];
  sendMessage: (text: string) => void;
  status: ChatStatus;
  error: Error | undefined;
  regenerate: () => void;
  stop: () => void;
  reset: () => void;
  isLoadingHistory: boolean;
  hasLoadError: boolean;
  threadId: string;
};

type UseResearchChatOptions = {
  threadId?: string;
  disabledSources?: string[];
};

export function useResearchChat({
  threadId,
  disabledSources,
}: UseResearchChatOptions) {
  const [initialMessages, setInitialMessages] = useState<ChatUIMessage[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | undefined>();

  const transport = useMemo(
    () =>
      new ResearchTransport({
        api: RESEARCH_MODE_AGUI_ENDPOINT,
        credentials: 'include',
      }),
    [],
  );

  // Update disabledSources on the transport instance when it changes
  // This keeps the transport instance stable for useChat while ensuring current values are used
  useEffect(() => {
    transport.setDisabledSources(disabledSources);
  }, [transport, disabledSources]);

  const [fetchThread] = useLazyGetResearchThreadQuery();

  // Track the last loaded threadId to prevent duplicate fetches
  const lastLoadedThreadIdRef = useRef<string | undefined>();

  // Derive loading state synchronously - true when we have a threadId that hasn't been loaded yet
  // This avoids the timing issue where useState(false) doesn't update until after the first render
  const isLoadingHistory = Boolean(
    threadId && threadId !== lastLoadedThreadIdRef.current,
  );

  // Use the prop threadId when provided (viewing a specific thread),
  // fall back to currentThreadId (for new conversations), or generate UUID
  const effectiveThreadId = useMemo(
    () => threadId || currentThreadId || crypto.randomUUID(),
    [currentThreadId, threadId],
  );

  const {
    messages,
    sendMessage: sendMessageRaw,
    status,
    error,
    regenerate,
    stop,
    setMessages,
  } = useChat<ChatUIMessage>({
    id: effectiveThreadId,
    transport,
    messages: initialMessages,
  });

  // Load thread when threadId changes
  // This effect must be after useChat so we can access setMessages
  useEffect(() => {
    const loadThread = async (targetThreadId: string) => {
      try {
        const { data: threadData } = await fetchThread({
          threadId: targetThreadId,
        });

        if (threadData?.messages && threadData.messages.length > 0) {
          const loadedMessages = convertResearchApiMessagesToUIMessages(
            threadData.messages,
          );

          // Update both initialMessages (for future reinitializations) and
          // setMessages (to update the current useChat instance)
          setInitialMessages(loadedMessages);
          setMessages(loadedMessages);
        } else {
          setInitialMessages([]);
          setMessages([]);
        }

        // Now update the thread ID so useChat uses the new ID with the loaded messages
        setCurrentThreadId(targetThreadId);
        lastLoadedThreadIdRef.current = targetThreadId;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load thread:', error);
        setInitialMessages([]);
        setMessages([]);
        setCurrentThreadId(targetThreadId);
        lastLoadedThreadIdRef.current = targetThreadId;
      }
    };

    if (threadId && threadId !== lastLoadedThreadIdRef.current) {
      loadThread(threadId);
    } else if (!threadId) {
      // Clear the ref when switching away from a modern thread (threadId becomes undefined)
      // This ensures that returning to the same thread will refetch its data
      lastLoadedThreadIdRef.current = undefined;
    }
  }, [threadId, fetchThread, setMessages]);

  // Explicit reset function for starting a new chat
  const reset = useCallback(() => {
    setInitialMessages([]);
    setMessages([]);
    // Generate new UUID immediately so useChat gets a fresh instance
    // This avoids relying on the threadId prop (from Redux) which updates async
    setCurrentThreadId(crypto.randomUUID());
    // Note: We intentionally do NOT clear lastLoadedThreadIdRef here.
    // Keeping it set prevents the loadThread effect from re-fetching the old
    // threadId before Redux state updates. The next explicit thread selection
    // will naturally update the ref.
  }, [setMessages]);

  const sendMessage = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      sendMessageRaw(createUserMessage(text));
    },
    [sendMessageRaw],
  );

  return {
    messages,
    sendMessage,
    status,
    error,
    regenerate,
    stop,
    reset,
    isLoadingHistory,
    // Research chat advances lastLoadedThreadIdRef on both success and failure,
    // so a failed load isn't distinguishable here. Always false; included to
    // match the shape of UseAnswerChatReturnType for union-type consumers.
    hasLoadError: false,
    threadId: effectiveThreadId,
  };
}
